/**
 * Terms of Service - The Scorekeeper
 * Effective May 12, 2026.
 */
import { Link } from 'react-router-dom';
import { paths } from '../lib/paths';

export function TermsOfServicePage() {
  return (
    <article className="m-article">
      <div className="eyebrow">Legal</div>
      <h1 className="h1">Terms of Service</h1>
      <p className="lede">Effective date: May 12, 2026</p>

      <p>
        These Terms of Service ("Terms") govern access to and use of The
        Scorekeeper, including thescorekeeper.ai, related websites, software,
        hosted pages, league tools, tournament tools, registration, scoring,
        communications, analytics, and other services we provide (the
        "Service").
      </p>

      <p>
        By accessing or using the Service, you agree to these Terms and our
        <Link to={paths.privacy}> Privacy Policy</Link>. If you are using the Service on
        behalf of a league, rink, tournament, team, company, or other
        organization, you represent that you have authority to bind that
        organization to these Terms.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>1. The Service</h2>
      <p>
        The Scorekeeper is a roller hockey league operating system. The Service
        may include tools for registration, rosters, waivers, payments,
        scheduling, scorekeeping, standings, player statistics, team pages,
        public event pages, communications, reporting, analytics, and
        AI-assisted league insights.
      </p>
      <p>
        Some features may be experimental, newly released, or available only to
        certain leagues. We may add, change, limit, suspend, or discontinue
        features as the product develops.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>2. Eligibility and accounts</h2>
      <p>
        You must be at least 18 years old, or the age of majority where you
        live, to create an administrator account or enter into these Terms on
        behalf of an organization. Minors may use player, captain, or
        participant-facing features only with permission from a parent or legal
        guardian and the applicable league or tournament operator. Children
        under 13 may not create accounts.
      </p>
      <p>
        You are responsible for keeping your account secure, for activity that
        occurs through your account, and for promptly telling us if you believe
        your account or league data has been compromised.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>3. League operator responsibilities</h2>
      <p>
        League and tournament operators are responsible for how they configure
        and use the Service. This includes responsibility for:
      </p>
      <ul className="m-bullets">
        <li>Ensuring they have the right to enter, upload, publish, message, and process participant, staff, family, and team information.</li>
        <li>Obtaining any required consents, permissions, waivers, media releases, and youth participant authorizations.</li>
        <li>Setting and communicating league rules, eligibility rules, refund policies, payment terms, safety policies, and event procedures.</li>
        <li>Reviewing schedules, standings, scores, rosters, reports, communications, AI-assisted outputs, and analytics before relying on them.</li>
        <li>Managing admin access and removing access for people who should no longer have it.</li>
        <li>Complying with laws and regulations that apply to their league, rink, tournament, participants, payments, communications, and data.</li>
      </ul>

      <h2 className="h2" style={{ marginTop: 32 }}>4. User content and league data</h2>
      <p>
        You keep ownership of the content and data you provide to the Service,
        including league information, team information, roster information,
        schedules, scores, statistics, messages, uploaded materials, and other
        content ("User Content").
      </p>
      <p>
        You grant us a limited right to host, store, copy, process, display,
        transmit, modify for formatting or technical compatibility, and use User
        Content as needed to provide, secure, support, and improve the Service.
        This includes making schedules, rosters, scores, standings, statistics,
        team pages, and event pages available to the audiences configured by the
        applicable league or tournament operator.
      </p>
      <p>
        You are responsible for the accuracy and legality of User Content you
        provide. Do not upload or publish content that you do not have the right
        to use.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>5. Payments, subscriptions, and refunds</h2>
      <p>
        Paid features may require subscription fees, platform fees, registration
        fees, processing fees, or other charges. Payment processing may be
        provided by Stripe or another payment processor. By using payment
        features, you agree to the applicable processor's terms and policies.
      </p>
      <p>
        League subscriptions, negotiated pricing, platform fees, and payment
        arrangements may be described in an order form, written agreement,
        invoice, checkout page, or in-product billing screen. Unless a separate
        written agreement says otherwise, fees are due when charged and are
        non-refundable except as required by law.
      </p>
      <p>
        League and tournament operators are responsible for their own player
        registration pricing, refund policies, chargeback handling, tax
        obligations, payout account accuracy, and communications with
        registrants about payments and refunds.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>6. AI-assisted and analytics features</h2>
      <p>
        The Service may provide AI-assisted schedules, reports, recaps,
        recommendations, scouting notes, analytics, or other generated outputs.
        These features are intended to support human decision-making. They may
        be incomplete, inaccurate, or based on data that was entered
        incorrectly.
      </p>
      <p>
        You are responsible for reviewing AI-assisted and analytics outputs
        before using them for league operations, scheduling decisions,
        eligibility decisions, disciplinary decisions, payments, or public
        communications.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>7. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul className="m-bullets">
        <li>Use the Service for unlawful, harmful, fraudulent, abusive, or misleading activity.</li>
        <li>Interfere with, overload, scan, scrape, reverse engineer, or attempt to bypass the Service's security or access controls.</li>
        <li>Upload malware or use the Service to send spam, phishing messages, or unauthorized communications.</li>
        <li>Post or transmit content that infringes intellectual property, privacy, publicity, or other rights.</li>
        <li>Use another person's account without permission or misrepresent your identity or authority.</li>
        <li>Use the Service in a way that creates unreasonable risk to players, families, staff, spectators, or us.</li>
      </ul>

      <h2 className="h2" style={{ marginTop: 32 }}>8. Public pages and communications</h2>
      <p>
        The Service may publish event pages, team pages, schedules, rosters,
        standings, scores, statistics, leaderboards, and communications. League
        operators are responsible for deciding what should be public, what
        should be restricted, and what should not be entered into the Service.
      </p>
      <p>
        Communications sent through the Service may be viewed by intended
        recipients, admins, service providers, or others depending on how a
        feature is configured. Do not use the Service for emergency
        communications where delay or delivery failure could create risk.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>9. Third-party services</h2>
      <p>
        The Service may integrate with or link to third-party services such as
        Google, Cloudflare, Stripe, communication providers, or other tools.
        Third-party services are governed by their own terms and policies. We
        are not responsible for third-party services we do not control.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>10. Intellectual property</h2>
      <p>
        The Service, including its software, design, branding, workflows,
        documentation, and other materials, is owned by us or our licensors and
        is protected by intellectual property laws. These Terms do not transfer
        ownership of the Service to you. You may not copy, modify, distribute,
        sell, lease, or create derivative works from the Service except as
        allowed by us in writing.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>11. Product feedback</h2>
      <p>
        We welcome feedback, ideas, wishlist items, design comments, and feature
        requests from roller hockey leagues. If you send us feedback, you allow
        us to use it without restriction or compensation. We are not obligated
        to build, release, maintain, or support any requested feature unless a
        separate written agreement says otherwise.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>12. Suspension and termination</h2>
      <p>
        We may suspend or terminate access to the Service if we believe you have
        violated these Terms, created security or legal risk, failed to pay
        amounts owed, or used the Service in a way that could harm users,
        leagues, the Service, or us. You may stop using the Service at any time.
        Certain provisions of these Terms will survive termination, including
        payment obligations, intellectual property terms, disclaimers,
        limitations of liability, indemnity, and provisions that by their nature
        should survive.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>13. Disclaimers</h2>
      <p>
        The Service is provided "as is" and "as available." To the fullest
        extent permitted by law, we disclaim warranties of merchantability,
        fitness for a particular purpose, non-infringement, availability,
        accuracy, and reliability.
      </p>
      <p>
        We do not guarantee that schedules, scores, standings, communications,
        registrations, payment records, analytics, AI-assisted outputs, or other
        Service outputs will be error-free or uninterrupted. The Service is not
        a substitute for league judgment, official game records, safety
        procedures, legal advice, tax advice, medical advice, or emergency
        services.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>14. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, The Scorekeeper will not be
        liable for indirect, incidental, special, consequential, exemplary, or
        punitive damages, or for lost profits, lost revenue, lost data, business
        interruption, reputational harm, or the cost of substitute services.
      </p>
      <p>
        To the fullest extent permitted by law, our total liability for any
        claim related to the Service will not exceed the amounts paid to us by
        the customer for the Service during the three months before the event
        giving rise to the claim, or one hundred dollars if no amounts were paid
        to us.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>15. Indemnity</h2>
      <p>
        To the fullest extent permitted by law, you agree to defend, indemnify,
        and hold harmless The Scorekeeper from claims, damages, liabilities,
        costs, and expenses arising from your use of the Service, your User
        Content, your violation of these Terms, your violation of law, your
        payment or refund obligations, or your violation of another person's
        rights.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>16. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will update
        the effective date. Continued use of the Service after updated Terms are
        posted means you accept the updated Terms.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>17. Conflicting agreements</h2>
      <p>
        If you have a signed order form, statement of work, enterprise
        agreement, or other written agreement with us, that agreement controls
        to the extent it conflicts with these Terms.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>18. Contact</h2>
      <p>
        Questions about these Terms can be sent through the
        <Link to={paths.contact}> Contact page</Link>.
      </p>
    </article>
  );
}
